// Money as whole minor units (kobo, cents), never a floating-point amount,
// so totals are exact and match the phone app to the kobo. A port of
// packages/paidlark_core/lib/data/models/money.dart; the shared cases in
// packages/paidlark_core/test_vectors keep the two in step.

export interface Currency {
  /** ISO 4217 code, e.g. NGN. */
  code: string;
  symbol: string;
  /** Minor-unit digits: 2 for kobo and cents. */
  decimals: number;
}

export const CURRENCIES: readonly Currency[] = [
  { code: 'NGN', symbol: '₦', decimals: 2 },
  { code: 'USD', symbol: '$', decimals: 2 },
  { code: 'GBP', symbol: '£', decimals: 2 },
  { code: 'EUR', symbol: '€', decimals: 2 },
  { code: 'GHS', symbol: 'GH₵', decimals: 2 },
  { code: 'KES', symbol: 'KSh', decimals: 2 },
  { code: 'ZAR', symbol: 'R', decimals: 2 },
];

/** A supported currency; NGN for unknown codes. */
export function currencyOf(code: string | null | undefined): Currency {
  const upper = (code ?? '').toUpperCase();
  return CURRENCIES.find((c) => c.code === upper) ?? CURRENCIES[0];
}

const factor = (currency: Currency) => 10 ** currency.decimals;

/** Rounds half away from zero, as Dart's `round()` does. */
export function roundHalfAway(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

export class Money {
  constructor(
    readonly minor: number,
    readonly currency: Currency,
  ) {}

  static zero(currency: Currency): Money {
    return new Money(0, currency);
  }

  /** For constants and tests; typed amounts go through [tryParse]. */
  static fromMajor(major: number, currency: Currency): Money {
    return new Money(roundHalfAway(major * factor(currency)), currency);
  }

  /**
   * Parses "25,000", "₦78,250.50" or "78250.5" straight to minor units with
   * no floating-point step. Digits past the currency's decimals round half
   * up. Null unless it is a valid amount of zero or more.
   */
  static tryParse(text: string, currency: Currency): Money | null {
    const cleaned = text.replace(/[^0-9.]/g, '');
    const match = /^(\d*)(?:\.(\d*))?$/.exec(cleaned);
    if (!match) return null;
    const whole = match[1];
    const fraction = match[2] ?? '';
    if (whole === '' && fraction === '') return null;
    const kept = fraction.padEnd(currency.decimals + 1, '0');
    let minor = Number(whole === '' ? '0' : whole) * factor(currency);
    if (currency.decimals > 0) minor += Number(kept.substring(0, currency.decimals));
    if (kept.charCodeAt(currency.decimals) >= 0x35) minor += 1; // '5' or more
    if (!Number.isSafeInteger(minor)) return null;
    return new Money(minor, currency);
  }

  get isZero(): boolean {
    return this.minor === 0;
  }

  get isNegative(): boolean {
    return this.minor < 0;
  }

  get isPositive(): boolean {
    return this.minor > 0;
  }

  get major(): number {
    return this.minor / factor(this.currency);
  }

  plus(other: Money): Money {
    this.check(other);
    return new Money(this.minor + other.minor, this.currency);
  }

  minus(other: Money): Money {
    this.check(other);
    return new Money(this.minor - other.minor, this.currency);
  }

  negate(): Money {
    return new Money(-this.minor, this.currency);
  }

  /**
   * Multiplies by a quantity (1.5 hours; two decimals count), rounding half
   * away from zero. The quantity becomes whole hundredths first, so
   * 0.29 × ₦100.00 is ₦29.00, not ₦28.99.
   */
  times(quantity: number): Money {
    const product = BigInt(this.minor) * BigInt(roundHalfAway(quantity * 100));
    return new Money(Number(divideHalfAway(product, 100n)), this.currency);
  }

  /** A rate in basis points (750 = 7.5%), rounding half away from zero. */
  percent(basisPoints: number): Money {
    const product = BigInt(this.minor) * BigInt(basisPoints);
    return new Money(Number(divideHalfAway(product, 10000n)), this.currency);
  }

  min(other: Money): Money {
    return this.compareTo(other) <= 0 ? this : other;
  }

  max(other: Money): Money {
    return this.compareTo(other) >= 0 ? this : other;
  }

  compareTo(other: Money): number {
    this.check(other);
    return Math.sign(this.minor - other.minor);
  }

  equals(other: Money): boolean {
    return this.minor === other.minor && this.currency.code === other.currency.code;
  }

  /**
   * NGN hides kobo when it is zero (₦485,000) and shows two decimals when it
   * is not (₦78,250.50); other currencies always show two ($1,200.00).
   * Negative amounts get a minus sign (−₦6,000).
   */
  format({ withSymbol = true }: { withSymbol?: boolean } = {}): string {
    const f = factor(this.currency);
    const abs = Math.abs(this.minor);
    const hideDecimals = (this.currency.code === 'NGN' && abs % f === 0) || this.currency.decimals === 0;
    const digits = hideDecimals ? 0 : this.currency.decimals;
    const number = groupDigits(Math.trunc(abs / f)) + (digits > 0 ? '.' + String(abs % f).padStart(digits, '0') : '');
    const sign = this.isNegative ? '−' : '';
    return withSymbol ? `${sign}${this.currency.symbol}${number}` : `${sign}${number}`;
  }

  /** Short form for stat cards: ₦640k, ₦3.2M; under 1,000 in full. */
  formatCompact(): string {
    const value = Math.abs(this.minor) / factor(this.currency);
    const sign = this.isNegative ? '−' : '';
    const trim = (v: number) => {
      const s = v.toFixed(1);
      return s.endsWith('.0') ? s.slice(0, -2) : s;
    };
    const symbol = this.currency.symbol;
    if (value >= 1e9) return `${sign}${symbol}${trim(value / 1e9)}B`;
    if (value >= 1e6) return `${sign}${symbol}${trim(value / 1e6)}M`;
    if (value >= 1e3) return `${sign}${symbol}${trim(value / 1e3)}k`;
    return this.format();
  }

  toString(): string {
    return this.format();
  }

  private check(other: Money) {
    if (other.currency.code !== this.currency.code) {
      throw new Error(`Cannot combine ${this.currency.code} with ${other.currency.code}`);
    }
  }
}

function divideHalfAway(value: bigint, by: bigint): bigint {
  const abs = value < 0n ? -value : value;
  const rounded = (abs * 2n + by) / (by * 2n);
  return value < 0n ? -rounded : rounded;
}

function groupDigits(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** "7.5%" from 750, "5%" from 500. */
export function formatBasisPoints(basisPoints: number): string {
  const whole = Math.trunc(basisPoints / 100);
  const rest = basisPoints % 100;
  if (rest === 0) return `${whole}%`;
  const decimals = rest % 10 === 0 ? String(rest / 10) : String(rest).padStart(2, '0');
  return `${whole}.${decimals}%`;
}

/** 750 from "7.5" or "7.5%"; null unless 0–100 with at most two decimals. */
export function parseBasisPoints(text: string): number | null {
  const match = /^(\d{1,3})(?:\.(\d{1,2}))?%?$/.exec(text.trim());
  if (!match) return null;
  const fraction = (match[2] ?? '').padEnd(2, '0');
  const basisPoints = Number(match[1]) * 100 + Number(fraction);
  return basisPoints <= 10000 ? basisPoints : null;
}

/** "2" for 2, "1.5" for 1.5: quantities without trailing zeros. */
export function formatQuantity(quantity: number): string {
  return quantity.toFixed(2).replace(/\.?0+$/, '');
}

/** A typed quantity ("2", "1.5", "0.25"); null unless above zero with at most two decimals. */
export function parseQuantity(text: string): number | null {
  const match = /^\d{1,6}(\.\d{1,2})?$/.exec(text.trim());
  if (!match) return null;
  const value = Number(match[0]);
  return value > 0 ? value : null;
}
